/**
 * Reading the member's own claim rows as two answers the deposit screen needs:
 * which promotion the NEXT deposit will spend, and which bonus a deposit ALREADY
 * made produced.
 *
 * Both are read-only joins over `GET /promotion-claims/me`. svc-finance holds no
 * promotion reference — `finance.deposits` has no promo column and must not grow
 * one — so the link lives on the claim row (`deposit_id`) and is followed here.
 */
import { promotionClaimOutcome } from '@b1fr0st/contracts/domain/promo-claim-status';
import { bigIntish, type PromotionClaim } from './api.js';

/** Epoch ms of `createdAt`, or +Infinity when the row carries no usable stamp —
 *  an unordered row sorts last rather than jumping the queue. */
function createdAtMs(claim: PromotionClaim): number {
  const ms = claim.createdAt ? Date.parse(claim.createdAt) : Number.NaN;
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

/** The two campaign kinds a deposit can upgrade a reservation on — the
 *  `p.kind IN (...)` half of `findPendingReservations`. */
const UPGRADABLE_KINDS: ReadonlySet<string> = new Set(['first_deposit', 'reload']);

/**
 * The reservation svc-promo would spend on this member's next approved deposit,
 * or null when they have opted into nothing.
 *
 * The predicate is the union of the two the server applies, reduced to fields
 * that are on the claim row `/me` returns:
 *
 * - `findOpenReservation` (apps/promo/src/repo/promotions.ts) — 'pending', no
 *   operator decision outstanding, nothing granted, no deposit and no wallet
 *   transaction against it yet, not past its own expiry. The two that look
 *   redundant are not: `dropBonusBulk` writes 'pending' over a credit that
 *   already landed (non-zero grant), and an approval-queue claim is 'pending'
 *   with `awaitingCredit` set. Neither is a thing a deposit can trigger.
 * - `findPendingReservations` — the function that actually decides what a
 *   deposit spends. It additionally requires the joined campaign to be
 *   `kind IN ('first_deposit','reload')` and `status = 'active'`. That last one
 *   is load-bearing and not theoretical: `runReservationSweep` deliberately
 *   leaves a paused campaign's reservations alive, so a claim can sit 'pending'
 *   indefinitely against a campaign no deposit will ever upgrade it on. Naming
 *   it would promise a bonus the transfer cannot receive.
 *
 * Both joined fields are treated as "not known" when absent rather than "not
 * eligible": this client and svc-promo deploy on independent pipelines, and a
 * client that ships first must degrade to its previous answer, never to a
 * blanket "no promotion" for every player (the same reason `CheckinStreak`
 * marks its own late-added fields optional).
 *
 * Oldest first, because `findPendingReservations` orders by `created_at` ASC and
 * the deposit consumer upgrades exactly one reservation per deposit. Naming a
 * different one would tell the player the wrong promotion is about to be spent.
 */
export function findDepositReservation(
  claims: PromotionClaim[] | undefined,
  nowMs: number,
): PromotionClaim | null {
  if (!claims?.length) return null;
  const open = claims.filter((c) => {
    if (c.status !== 'pending') return false;
    // `!== true`, not `=== false`: the column is NOT NULL server-side, but a
    // field this client cannot see must not silence a tag whose absence is the
    // very mistake the tag exists to prevent.
    if (c.awaitingCredit === true) return false;
    if (bigIntish(c.grantedAmount) !== 0n) return false;
    if (c.depositId != null) return false;
    if (c.txId != null) return false;
    if (c.promotionStatus != null && c.promotionStatus !== 'active') return false;
    if (c.promotionKind != null && !UPGRADABLE_KINDS.has(c.promotionKind)) return false;
    if (c.expiresAt != null) {
      const expiry = Date.parse(c.expiresAt);
      // An unparseable stamp is not an expiry — dropping the reservation on one
      // would hide an opt-in the server still honours.
      if (Number.isFinite(expiry) && expiry <= nowMs) return false;
    }
    return true;
  });
  if (open.length === 0) return null;
  return open.reduce((oldest, c) => (createdAtMs(c) < createdAtMs(oldest) ? c : oldest));
}

/**
 * Deposit id → the bonus claim that deposit produced, for the recent-deposits
 * list.
 *
 * Only claims whose status means the principal actually reached the player's
 * pool are indexed. `granted_amount` survives a forfeit, a rejection and an
 * admin clawback completely unchanged, so branching on the amount alone would
 * draw a green bonus beside a deposit whose bonus was taken back
 * (`PROMOTION_CLAIM_OUTCOME`). A status this build has never heard of resolves
 * to 'awaiting' and is skipped for the same reason.
 *
 * A deposit grants at most one bonus — `promo.processed_deposit_grants` makes
 * that a database fact — so a second row on the same deposit is a repair
 * artefact, not a second bonus to announce. The first entry written wins, and
 * because `listClaimsByMember` orders `created_at` DESC that is the NEWEST of
 * the two: the repair, rather than the row it replaced.
 */
export function indexBonusByDeposit(
  claims: PromotionClaim[] | undefined,
): Map<string, PromotionClaim> {
  const byDeposit = new Map<string, PromotionClaim>();
  for (const claim of claims ?? []) {
    if (!claim.depositId) continue;
    if (promotionClaimOutcome(claim.status) !== 'credited') continue;
    if (bigIntish(claim.grantedAmount) <= 0n) continue;
    if (!byDeposit.has(claim.depositId)) byDeposit.set(claim.depositId, claim);
  }
  return byDeposit;
}
